import { Controller } from '@hotwired/stimulus';

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default class extends Controller {
  static targets = ['dialog'];

  connect() {
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    document.addEventListener('click', this.handleTriggerClick);

    // A dialog can arrive already open (`open: true`), e.g. re-rendered by a
    // turbo_stream after a failed form submission. Acquire the same state
    // open() would have set up.
    if (!this.element.hidden) {
      document.body.style.overflow = 'hidden';
      this.scrollLocked = true;
      document.addEventListener('keydown', this.handleKeydown);

      requestAnimationFrame(() => {
        if (this.hasDialogTarget) this.dialogTarget.focus();
      });
    }
  }

  disconnect() {
    document.removeEventListener('click', this.handleTriggerClick);
    document.removeEventListener('keydown', this.handleKeydown);
    if (this.scrollLocked) {
      document.body.style.overflow = '';
      this.scrollLocked = false;
    }
  }

  open() {
    if (!this.element.hidden) return;

    this.previousFocus = document.activeElement;
    this.element.classList.remove('ui-dialog-overlay--closing');
    this.element.hidden = false;
    document.body.style.overflow = 'hidden';
    this.scrollLocked = true;
    document.addEventListener('keydown', this.handleKeydown);

    requestAnimationFrame(() => {
      if (this.hasDialogTarget) this.dialogTarget.focus();
    });

    this.dispatch('open');
  }

  confirm() {
    this.dispatch('confirm');
    this.requestClose();
  }

  requestClose() {
    if (this.element.hidden) return;
    this.element.classList.add('ui-dialog-overlay--closing');
  }

  animationEnd(event) {
    if (event.target !== this.element) return;
    if (!this.element.classList.contains('ui-dialog-overlay--closing')) return;

    this.element.classList.remove('ui-dialog-overlay--closing');
    this.element.hidden = true;
    document.body.style.overflow = '';
    this.scrollLocked = false;
    document.removeEventListener('keydown', this.handleKeydown);

    if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
      this.previousFocus.focus();
    }

    this.dispatch('close');
  }

  backdropClick(event) {
    if (event.target === this.element) {
      this.requestClose();
    }
  }

  handleTriggerClick(event) {
    const trigger = event.target.closest(`[data-dialog-trigger="${this.element.id}"]`);
    if (!trigger) return;
    event.preventDefault();
    this.open();
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.requestClose();
      return;
    }

    if (event.key !== 'Tab' || !this.hasDialogTarget) return;

    const focusable = this.dialogTarget.querySelectorAll(FOCUSABLE_SELECTOR);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}
